import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, FormInstance, TextInput } from 'blueprint-form';
import { faBell, faEye, faSignInAlt } from '@fortawesome/pro-solid-svg-icons';

import Icon from '../../atoms/icon';
import { PortalContainer } from '../../atoms/portal-container';
import Button from '../../molecules/button';
import Card from '../../organisms/card';
import Callout from '../../organisms/callout';
import ErrorCallout from '../../organisms/callout/error-callout';
import { useAuth } from '../../../contexts/auth';
import { IAlert, useGetAlert } from '../../../libs/alert';
import { PostJSON, BaseResponse } from '../../../libs/request';
import { FormatDateTime } from '../../../libs/time';
import { GetFullName } from '../../../libs/patient';
import { FormatPhone } from '../../../libs/format';

interface AlertCardProps {
  alert: IAlert;
  isAuthenticated?: boolean;
}

const AlertCard: React.FC<AlertCardProps> = ({ alert, isAuthenticated }) => {
  return (
    <Card>
      <h1 className="text-sans-lg mb-7">
        <Icon icon={faBell} /> View Alert
      </h1>
      <Callout intent="primary" title="Details">
        <p>{alert.body}</p>
        <p className="text-small font-sans">
          Triggered at: <b>{FormatDateTime(alert.createdAt)}</b>
        </p>
      </Callout>
      {alert.patient && isAuthenticated ? (
        <Card className="mt-4">
          CSID: <b>{alert.patient.id}</b>
          <br />
          Name: <b>{GetFullName(alert.patient)}</b>
          <br />
          MRN: <b>{alert.patient.mrn}</b>
          <br />
          Phone:&nbsp;
          <b>
            <a href={`tel:${alert.patient.phone}`}>
              {FormatPhone(alert.patient.phone)}
            </a>
          </b>
        </Card>
      ) : null}
      <Button
        className="mt-4"
        intent="primary"
        linkTo={`/patient/${alert.patientId}`}
      >
        View Patient Profile
      </Button>
    </Card>
  );
};

interface AuthedAlertViewProps {
  alertId: number;
}

const AuthedAlertView: React.FC<AuthedAlertViewProps> = ({ alertId }) => {
  const { alert } = useGetAlert(alertId);
  if (!alert) {
    return null;
  }
  return <AlertCard alert={alert} isAuthenticated />;
};

interface ProtectedAlertViewProps {
  alertId: number;
}

interface ViewAlertData {
  pin: string;
}

interface GetAlertResponse extends BaseResponse {
  alert?: IAlert;
}

const ProtectedAlertView: React.FC<ProtectedAlertViewProps> = ({ alertId }) => {
  const [alert, setAlert] = useState<IAlert>(null);
  const [error, setError] = useState<string>(null);
  const form = new FormInstance<ViewAlertData>({
    pin: ''
  });
  const getAlert = (data: ViewAlertData) => {
    if (!(data.pin?.length >= 4)) {
      return;
    }
    PostJSON<GetAlertResponse>({
      url: '/alert/view/',
      params: {
        id: alertId,
        pin: data.pin
      }
    }).then((response) => {
      if (response.status !== 'OK') {
        setError(response.message);
        return;
      }
      const _alert = (response as GetAlertResponse).alert;
      if (!_alert) {
        setError('The alert does not exist in the system!');
        return;
      }
      setAlert(_alert);
      setError(null);
    });
  };
  return (
    <React.Fragment>
      {!alert ? (
        <Card>
          <h1 className="text-sans-lg mb-7">
            <Icon icon={faBell} /> View Alert
          </h1>
          <Form form={form} onSubmit={getAlert}>
            {error && <ErrorCallout>{error}</ErrorCallout>}
            <TextInput
              label="Account PIN (min. 4 Digits)"
              type="password"
              name="pin"
              pattern={/^[0-9]{4,}$/}
              patternError="At least 4 digits are required!"
              fill
            />
            <Callout>
              You may enter your account PIN to quickly view the alert. You have
              3 attempts to enter the correct PIN, after which you must login to
              view.
            </Callout>
            <Button
              className="mt-4"
              intent="primary"
              fill
              type="submit"
              icon={<Icon className="text-white" icon={faEye} />}
            >
              Quick Access
            </Button>
          </Form>
        </Card>
      ) : (
        <AlertCard alert={alert} />
      )}
      <Card className="mt-4">
        <p>Alternatively, login to see the full alert details:</p>
        <Button
          fill
          linkTo={`/alert/view/${alertId}`}
          icon={<Icon icon={faSignInAlt} />}
        >
          Login to CareSignal
        </Button>
      </Card>
      <PortalContainer />
    </React.Fragment>
  );
};

const AlertPage: React.FC = () => {
  const { user } = useAuth();
  const { alertId: _alertId } = useParams<{ alertId?: string }>();
  const alertId = _alertId ? parseInt(_alertId, 10) : null;
  if (!user) {
    return <ProtectedAlertView alertId={alertId} />;
  }
  return <AuthedAlertView alertId={alertId} />;
};

export default AlertPage;
